import { deskAttachmentUrl } from "services/desk.service";
import { DeskAttachmentItem } from "types/Desk";

const chipCls =
  "flex items-center gap-1 rounded-lg bg-zinc-100 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 max-md:min-h-11 max-md:px-3.5";

export function isPreviewable(mimeType: string | null): "image" | "pdf" | null {
  if (!mimeType) return null;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return null;
}

function formatSize(bytes: number): string {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  attachment: DeskAttachmentItem;
  companyId: string;
  isPhone: boolean;
  onPreview: (attachment: DeskAttachmentItem) => void;
}

/**
 * One attachment under a message: images and PDFs open the in-panel preview,
 * everything else is a plain download link. On a phone a PDF is a link too —
 * iOS Safari shows only the first page of a PDF inside an iframe, while its own
 * viewer in a new tab scrolls and zooms.
 */
export default function DeskAttachmentChip({ attachment, companyId, isPhone, onPreview }: Props) {
  const kind = isPreviewable(attachment.mimeType);
  const icon = kind === "image" ? "🖼️" : kind === "pdf" ? "📄" : "📎";
  const content = (
    <>
      {icon} {attachment.filename || attachment.id}
      <span className="text-zinc-500">{formatSize(attachment.sizeBytes)}</span>
    </>
  );

  if (kind && !(kind === "pdf" && isPhone)) {
    return (
      <button type="button" onClick={() => onPreview(attachment)} className={chipCls}>
        {content}
      </button>
    );
  }
  return (
    <a href={deskAttachmentUrl(companyId, attachment.id)} target="_blank" rel="noreferrer" className={chipCls}>
      {content}
    </a>
  );
}
